import { useUser } from "@clerk/tanstack-react-start";
import {
  createFileRoute,
  Outlet,
  Link,
  useLocation,
} from "@tanstack/react-router";

import { EncryptionGate } from "~/components/encryption-gate";
import { PageTitle } from "~/components/ui/page-title";
import { Skeleton } from "~/components/ui/skeleton";
import { CoachWhatsNewHereLink } from "~/components/whats-new/whats-new-here-link";
import { cn } from "~/lib/cn";
import { getHourInClientTimezone } from "~/lib/utils/timezone";

export const Route = createFileRoute("/_user/life-design")({
  staticData: { title: "Life By Design" },
  component: RouteComponent,
});

function RouteComponent() {
  const { user, isLoaded } = useUser();
  const location = useLocation();

  const getGreeting = () => {
    const hour = getHourInClientTimezone();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  const isGoals = location.pathname.startsWith("/life-design/goals");
  // Every other Life Design page, a category page included, is part of Vision.
  const tabs: Array<{
    name: string;
    path: string;
    isActive: boolean;
    disabled?: boolean;
  }> = [
    { name: "Vision", path: "/life-design", isActive: !isGoals },
    { name: "Goals", path: "/life-design/goals", isActive: isGoals },
  ];

  return (
    <div className="flex min-h-full flex-col pb-8">
      <div className="mb-4 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
        <div>
          {!isLoaded ? (
            <div className="flex items-center gap-2">
              <PageTitle>{getGreeting()},</PageTitle>
              <Skeleton className="h-8 w-32" />
            </div>
          ) : (
            <PageTitle>
              {getGreeting()}, {user?.firstName || "User"}
            </PageTitle>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <CoachWhatsNewHereLink module="life_by_design" />
          <div className="flex items-center rounded-full border border-gray-200 bg-white p-1 shadow-sm">
            {tabs.map((tab) => (
              <Link
                key={tab.name}
                to={tab.path}
                disabled={tab.disabled}
                className={cn(
                  "rounded-full px-6 py-2 text-sm font-medium transition",
                  tab.isActive
                    ? "bg-purple-100 text-purple-900 shadow-sm"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                  tab.disabled &&
                    "cursor-not-allowed opacity-50 hover:bg-transparent hover:text-gray-600",
                )}
              >
                {tab.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
      <main className="grow">
        <EncryptionGate>
          <Outlet />
        </EncryptionGate>
      </main>
    </div>
  );
}
